import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kSharePointSearchSitesPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { SharePointSiteSearchResponse, SharePointSiteSearchResponseSchema } from '@/user_files/external-storage/schemas'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

/** Search SharePoint sites for the connected user by name or keyword. */
export async function postSearchSharePointSites(params: { accountId: string; query: string }): Promise<SharePointSiteSearchResponse> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kSharePointSearchSitesPath}`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify({ account_id: params.accountId, query: params.query }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            accountId: params.accountId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to search SharePoint sites: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Search SharePoint sites response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            accountId: params.accountId,
            parseError: formatUnknownError(parseError),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid search SharePoint sites response JSON')
    }

    const parsed = SharePointSiteSearchResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Search SharePoint sites response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            accountId: params.accountId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid search SharePoint sites response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { accountId: params.accountId } })
      })
    }
    throw error
  }
}
